import { DateTime } from 'luxon';

const DEFAULT_TIMEZONE = 'America/Chicago';

function normalizeTimezone(location) {
  const tzCandidate = location?.timezone || location?.timeZone || location?.tz || DEFAULT_TIMEZONE;
  const dt = DateTime.now().setZone(tzCandidate);
  return dt.isValid ? tzCandidate : DEFAULT_TIMEZONE;
}

function parseCutoffMinutes(location) {
  const cutoff = location?.businessDayCutoff;
  if (cutoff && typeof cutoff === 'object') {
    const hour = Number(cutoff.hour);
    const minute = Number(cutoff.minute);
    if (!Number.isNaN(hour) && !Number.isNaN(minute)) {
      if (hour === 24 && minute === 0) return 24 * 60;
      if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
        return hour * 60 + minute;
      }
    }
  }

  const legacy = location?.endOfBusiness;
  if (typeof legacy === 'string') {
    if (legacy === '24:00') return 24 * 60;
    const [hhRaw, mmRaw] = legacy.split(':');
    const hour = Number(hhRaw);
    const minute = Number(mmRaw);
    if (!Number.isNaN(hour) && !Number.isNaN(minute) && hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
      return hour * 60 + minute;
    }
  }

  return 24 * 60;
}

function toUtcDateTime(timestampUTC) {
  if (timestampUTC && typeof timestampUTC.toDate === 'function') {
    const fromFsTimestamp = DateTime.fromJSDate(timestampUTC.toDate(), { zone: 'utc' });
    if (fromFsTimestamp.isValid) return fromFsTimestamp;
  }

  if (timestampUTC instanceof Date) {
    const fromDate = DateTime.fromJSDate(timestampUTC, { zone: 'utc' });
    if (fromDate.isValid) return fromDate;
  }

  if (typeof timestampUTC === 'number') {
    const fromMillis = DateTime.fromMillis(timestampUTC, { zone: 'utc' });
    if (fromMillis.isValid) return fromMillis;
  }

  if (typeof timestampUTC === 'string') {
    const fromIso = DateTime.fromISO(timestampUTC, { zone: 'utc' });
    if (fromIso.isValid) return fromIso;
  }

  throw new Error('Invalid timestampUTC passed to getBusinessDateKey');
}

/**
 * Convert a UTC timestamp to a location-specific business date key.
 * Rules:
 * - Convert UTC -> location timezone
 * - If local time is before location cutoff, subtract one day
 * - Return YYYY-MM-DD
 */
export function getBusinessDateKey(location, timestampUTC) {
  const timeZone = normalizeTimezone(location);
  const cutoffMinutes = parseCutoffMinutes(location);
  const utcDateTime = toUtcDateTime(timestampUTC);
  const localDateTime = utcDateTime.setZone(timeZone);
  const localMinutes = localDateTime.hour * 60 + localDateTime.minute;

  // 24:00 means standard calendar day behavior (no date roll-back).
  if (cutoffMinutes >= 24 * 60) {
    return localDateTime.toISODate();
  }

  return (localMinutes < cutoffMinutes ? localDateTime.minus({ days: 1 }) : localDateTime).toISODate();
}

